import {Tr} from "@/i18n/i18n";
import {Keys} from "@/constants/key";
import {
    AttackerEffect,
    BattleEffect,
    BattleMove,
    BattleStatus,
    BattleType,
    Effect,
    MovePrediction,
    NextMove,
    SkillDelta,
    TeamTag
} from "@/store/battle/types";
import {Creature, WarriorClass} from "@/store/level/types";
import {CreatureUtils} from "@/utils/creature-utils";
import CreaturePanel from "@/scenes.ui/creature-panel";
import {GridUtils} from "@/utils/grid-utils";
import {lazyInject} from "@/container";
import {TYPES} from "@/types";
import Arrow from "@/scenes.ui/arrow";
import {SceneService} from "@/services/scene-service";
import {BattleLessonService} from "@/services/battle-lesson-service";
import {SceneName} from "@/model/scene-name";
import {Settings} from "@/constants/settings";
import {BattleService} from "@/services/battle-service";
import SceneUtils from "@/utils/scene-utils";
import {AlertDialog} from "@/scenes.ui/alert-dialog";
import {YourTurnMessagePanel} from "@/scenes.ui/your-turn-message-panel";
import SoundUtils from "@/utils/sound-utils";
import {CreatureInteractionEffectTypePanel} from "@/scenes.ui/creature-interaction-effect-type-panel";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {ClanShield} from "@/scenes.ui.clan/clan-shield";
import BattleStore from "@/store/battle";
import PlayerStore from "@/store/player";
import {SkillType} from "@/store/place/types";
import {BattlePos, BattleUtils, IBattlePos, MultiBattlePos} from "@/utils/battle-utils";
import SpriteUtils from "@/utils/sprite-utils";
import {GameState} from "@/constants/game-state";
import {LoadUtils, PriorityType} from "@/utils/load-utils";
import CU from "@/utils/c-u";
import ListenerManager from "@/managers/listener-manager";
import {PhrasePanel} from "@/scenes.ui/phrase-panel";
import PlayerUtils from "@/utils/player-utils";
import {luckyClover} from "@/constants/battleSceneConstants";
import {PlaceService} from "@/services/place-service";
import {Reloadable} from "@/scenes/reloadable";
import SimplifiedCreaturePanel from "@/scenes.ui/simplified-creature-panel";
import Group = Phaser.GameObjects.Group;
import Sprite = Phaser.GameObjects.Sprite;
import Container = Phaser.GameObjects.Container;
import Image = Phaser.GameObjects.Image;
import Scene = Phaser.Scene;
import GameObject = Phaser.GameObjects.GameObject;

export default class BattleScene extends Scene implements Reloadable {

    @lazyInject(TYPES.BattleLessonService)
    private lessonService!: BattleLessonService;

    @lazyInject(TYPES.SceneService)
    private sceneService!: SceneService;

    @lazyInject(TYPES.BattleService)
    private battleService!: BattleService;

    private readonly ARROW_DEPTH: number = 999;
    private readonly MAX_CARD_QUANTITY: number = 10;

    private readonly CARD_FLY_UP_DURATION_MS: number = 120;
    private readonly CARD_TURN_DURATION_MS: number = 500;
    private readonly CARD_RETURN_DURATION_MS: number = 300;

    private skipButtonEnabled: boolean = false;

    private canvas!: HTMLCanvasElement;

    private background!: Sprite;

    private enemyCreaturesGroup!: Group;
    private myCreaturesGroup!: Group;

    private creaturePanelById: Map<number, CreaturePanel> = new Map();

    private chosenPlayerCreatureId: any = null;
    private chosenEnemyCreatureId: any = null;

    private cardArrow!: Arrow;

    private targetContainer: any = null;

    private battleResultWindowContainer!: Container | null;

    private mainThemeSound: Phaser.Sound.BaseSound | undefined;
    private victorySound: Phaser.Sound.BaseSound | undefined;
    private levelWonSound: Phaser.Sound.BaseSound | undefined;
    private defeatSound: Phaser.Sound.BaseSound | undefined;
    private windUpSound: Phaser.Sound.BaseSound | undefined;
    private windDownSound: Phaser.Sound.BaseSound | undefined;
    private clickSound: Phaser.Sound.BaseSound | undefined;

    private playerPunchSounds: (Phaser.Sound.BaseSound | undefined)[] = [];
    private enemyPunchSounds: (Phaser.Sound.BaseSound | undefined)[] = [];

    private yourTurnMessagePanel!: YourTurnMessagePanel;

    private luckyClover!: Image;
    private luckyCloverAnim!: any;

    private musicKey!: string;
    private isSpecialMusic = false;

    private moveNumber: number = 0;

    constructor() {
        super({ key: 'BattleScene' });
    }

    public preload(): void {
        SceneUtils.setCurrentScene(this);
        this.canvas = this.sys.game.canvas;

        let bs = BattleStore.getBattleStatus();
        if (bs && bs.battle && bs.battle.specialMusicKey) {
            if (PlayerUtils.hasMarker('p5_completed') && bs.battle.specialMusicKey === 'battle') {
                this.musicKey = Keys.MUSIC_BATTLE_4;
            } else if (PlayerUtils.hasMarker('p5_in_progress') && bs.battle.specialMusicKey === 'battle') {
                this.musicKey = Keys.MUSIC_BATTLE_3;
            } else {
                this.isSpecialMusic = true;
                this.musicKey = bs.battle.specialMusicKey;
            }
        } else {
            this.musicKey = Keys.MUSIC_BATTLE;
        }
    }

    private static getBackgoundKey(bs: BattleStatus): string {
        if (bs && bs.battle && bs.battle.backgroundKey) {
            return bs.battle.backgroundKey;
        }
        return Keys.IMG_BATTLE_BACKGROUND;
    }

    public create(): void {
        if (GameState.loadingType === PriorityType.BATTLE) {
            LoadUtils.prioritizedLoad(this, PriorityType.BATTLE);
            GameState.loadingType = undefined;
        }

        let self = this;
        self.creaturePanelById = new Map<number, CreaturePanel>();

        this.sceneService!.fadeBlackout(this, this.canvas!.width, this.canvas!.height);

        let bs = BattleStore.getBattleStatus();
        if (!bs) {
            this.sceneService.startScene(this, SceneName.ROAD_SCENE, this.canvas!.width, this.canvas!.height)
            return;
        }
        this.moveNumber = bs.moves.length;

        let backKey = BattleScene.getBackgoundKey(bs);
        let pos: IBattlePos = bs.battleType === BattleType.MULTI ? BattleUtils.getMultiPositions(this.canvas, backKey)
            : BattleUtils.getPositions(this.canvas, backKey);
        this.background = SpriteUtils.addSprite(this, 0, pos.getBackY(), backKey)
            .setOrigin(0, pos.getBackOriginY());
        SpriteUtils.addSprite(this, 0, 0, Keys.BATTLE_TOP_BOTTOM).setOrigin(0, 0);
        SpriteUtils.addSprite(this, 0, 0, Keys.BATTLE_TOP_BOTTOM).setOrigin(0, 0);
        SpriteUtils.addSprite(this, 0, CU.CANVAS_HEIGHT, Keys.BATTLE_TOP_BOTTOM).setScale(1, -1).setOrigin(0, 0);

        if (!!bs.battleName) {
            SpriteUtils.addSprite(this, 300, -15, Keys.ARENA_HEAD).setOrigin(0.5, 0);
            TextUtils.addTextAdvanced(this, bs.battleName, 300, 22, '#ffe14c', 14, 'normal',
                200, 'center', 2, Fonts.BENGALY_NORMAL);
        }

        this.myCreaturesGroup = this.add.group();
        this.enemyCreaturesGroup = this.add.group();

        this.cardArrow = this.makeArrow();
        this.add.existing(this.cardArrow);

        this.mainThemeSound = SoundUtils.addSound(this, this.musicKey, {volume: Settings.MUSIC_VOLUME});
        this.victorySound = SoundUtils.addSound(this, Keys.SOUND_VICTORY, {volume: Settings.MUSIC_VOLUME});
        this.levelWonSound = SoundUtils.addSound(this, Keys.SOUND_VICTORY_2, {volume: Settings.MUSIC_VOLUME});
        this.defeatSound = SoundUtils.addSound(this, Keys.SOUND_DEFEAT, {volume: Settings.SHORT_TRACK_VOLUME});
        this.windUpSound = SoundUtils.addSound(this, Keys.SOUND_WIND_UP);
        this.windDownSound = SoundUtils.addSound(this, Keys.SOUND_WIND_DOWN);

        this.playerPunchSounds = [];
        this.playerPunchSounds.push(SoundUtils.addSound(this, Keys.SOUND_PLAYER_PUNCH_1, {volume: Settings.SHORT_TRACK_VOLUME}));
        this.playerPunchSounds.push(SoundUtils.addSound(this, Keys.SOUND_PLAYER_PUNCH_2, {volume: Settings.SHORT_TRACK_VOLUME}));
        this.playerPunchSounds.push(SoundUtils.addSound(this, Keys.SOUND_PLAYER_PUNCH_3, {volume: Settings.SHORT_TRACK_VOLUME}));

        this.enemyPunchSounds = [];
        this.enemyPunchSounds.push(SoundUtils.addSound(this, Keys.SOUND_ENEMY_PUNCH_1, {volume: Settings.SHORT_TRACK_VOLUME}));
        this.enemyPunchSounds.push(SoundUtils.addSound(this, Keys.SOUND_ENEMY_PUNCH_2, {volume: Settings.SHORT_TRACK_VOLUME}));
        this.enemyPunchSounds.push(SoundUtils.addSound(this, Keys.SOUND_ENEMY_PUNCH_3, {volume: Settings.SHORT_TRACK_VOLUME}));

        this.clickSound = SoundUtils.addSound(this, Keys.SOUND_BUTTON_CLICK);
        SoundUtils.setLooped(this.mainThemeSound);
        SoundUtils.play(this.mainThemeSound);

        let initializePanelFunc = (creature: Creature, group: Group, isPlayerCreature: boolean) => {
            let panel = new CreaturePanel(this, 0, 0, creature, () => {
                self.spawnSkull(creature);
            }, isPlayerCreature, true);
            panel.setHealthVisible();
            panel.setCardShadeVisible();
            self.add.existing(panel);
            group.add(panel);
            self.creaturePanelById.set(creature.id, panel);
        };

        BattleStore.getMyCreatures().forEach((creature: Creature) => {
            initializePanelFunc(creature, self.myCreaturesGroup, true);
        });

        BattleStore.getEnemyCreatures().forEach((creature: Creature) => {
            initializePanelFunc(creature, self.enemyCreaturesGroup, false);
        });

        /**
         * Для того, чтобы карты с тень правильно располагались на сцене
         * (тень должна быть всегда позади своей и соседних карт)
         *  сделал ручное назначение глубины контейнеров -
         *  контейнеры врага имеют глубину с максимального количества карт (~10),
         *  контейнеры игрока имеют глубину с квадрата максимального количества карт (чтобы быть выше карт врага)
         *  глубина каждого последующего контейнера умньшается на 1
         * **/
        this.setGroupPanelsDepth(self.myCreaturesGroup, this.MAX_CARD_QUANTITY * 2);
        this.setGroupPanelsDepth(self.enemyCreaturesGroup, this.MAX_CARD_QUANTITY);

        this.battleService.createBattleGiveUpButton(this, this.canvas, (hasWon: boolean) => this.exitScene(bs!.battleType, hasWon),
            this.mainThemeSound, this.victorySound, this.defeatSound, this.clickSound);

        // It is just hidden for now. (it works, just uncomment and hide it for both Nuk-Serra and Rokkara battles)
        if (bs.battleType === BattleType.MULTI && (bs.battle.backgroundKey !== 'bbm_3' && bs.battle.backgroundKey !== 'bb_6' && bs.battle.backgroundKey !== 'bb_7')) {
            this.battleService.createSkipButton(this, this.canvas, () => {
                if (this.skipButtonEnabled) {
                    self.yourTurnMessagePanel.show(Tr('sk.mv'));
                    self.disableAllInteractions();
                    BattleStore.skip().then((bs: BattleStatus | undefined) => {
                        if (bs) {
                            self.processMoves(bs);
                        }
                    });
                }
            });
        }

        self.refreshCreaturesHealth(BattleStore.getMyCreatures());
        self.refreshCreaturesHealth(BattleStore.getEnemyCreatures());

        if (bs.battleType === BattleType.MULTI) {
            let p: MultiBattlePos = pos as MultiBattlePos;
            GridUtils.gridAlign(this.myCreaturesGroup, p.myY, this, true, p.myPosType);

            let t1: GameObject[] = this.enemyCreaturesGroup.getChildren().filter(cp => BattleUtils.findTeam(cp as CreaturePanel) === TeamTag.TM1);
            let t2: GameObject[] = this.enemyCreaturesGroup.getChildren().filter(cp => BattleUtils.findTeam(cp as CreaturePanel) === TeamTag.TM2);
            let t3: GameObject[] = this.enemyCreaturesGroup.getChildren().filter(cp => BattleUtils.findTeam(cp as CreaturePanel) === TeamTag.TM3);

            GridUtils.align(t1, TeamTag.TM1, p);
            GridUtils.align(t2, TeamTag.TM2, p);
            if (t3 && t3.length > 0) {
                GridUtils.align(t3, TeamTag.TM3, p);
            }
        } else {
            let p: BattlePos = pos as BattlePos;
            GridUtils.gridAlign(this.myCreaturesGroup, p.myY, this, true, p.myPosType);
            GridUtils.gridAlign(this.enemyCreaturesGroup, p.enemyY, this, false, p.enemyPosType);
        }

        this.battleService.createConsumablePanel(this, this.canvas, bs, this.creaturePanelById,(bs: BattleStatus) => {
            this.processMoves(bs);
        });

        if (!Settings.LOGGED_GAME_STARTED) {
            Settings.LOGGED_GAME_STARTED = true;
            PlayerStore.logGameStarted(true);
        }

        this.yourTurnMessagePanel = new YourTurnMessagePanel(this, this.canvas);
        this.add.existing(this.yourTurnMessagePanel);

        this.setUpPlayerCreaturesPreferences();
        this.setUpEnemyCreaturesPreferences(bs);

        this.lessonService.showBattleLesson(this, this.canvas!.width / 2, this.canvas!.height / 2,
            this.myCreaturesGroup, this.enemyCreaturesGroup);

        this.battleService.setupSceneDraggableActions(this);

        if (bs.scenarioMessage) {
            SpriteUtils.scale(new AlertDialog(this, this.canvas!, 300 * CU.S, this.canvas!.height / 2, Tr('soobschenie'), bs.scenarioMessage), 0.9).open();
        }

        this.showYourTurnMessage();

        if (bs.turningTeam && bs.turningTeam !== TeamTag.PLAYER) {
            this.disableAllInteractions();
            BattleStore.ask().then((bs: BattleStatus | undefined) => {
                if (bs) {
                    self.processMoves(bs);
                }
            });
        }

        self.luckyClover = SpriteUtils.makeImage(self, luckyClover);
        self.luckyCloverAnim = null;
    }

    private setUpPlayerCreaturesPreferences() {

        BattleStore.getMyCreatures().forEach((creature: Creature) => {

            if (CreatureUtils.getUnitHealthCurrentValue(creature) > 0) {
                // Для каждого живого существа разрешаем интеракции
                this.allowPlayerCreatureActions(creature);
                this.skipButtonEnabled = true;
            } else {
                // Каждому мертвому спавним черепок и запрещаем интеракции
                this.spawnSkull(creature);
            }
        })

        if (this.isAllCreaturesDead(BattleStore.getMyAliveCreatures())) {
            // Если нет живых, то пользователь проиграл
            // Запрещаем любые интеракции
            this.disableAllInteractions();
        }

        this.battleService.showBattleResultPanel(this, this.canvas, (hasWon: boolean) => this.exitScene(BattleType.MOB, hasWon),
            this.mainThemeSound, this.isSpecialMusic ? this.levelWonSound : this.victorySound, this.defeatSound);
    };

    private setUpEnemyCreaturesPreferences(bs: BattleStatus) {

        this.getEnemies(bs).forEach((creature: Creature) => {

            if (CreatureUtils.getUnitHealthCurrentValue(creature) > 0) {
                // Для каждого живого существа устанавливам интеракции
                this.allowEnemyCreatureActions(creature);
            } else {
                // Каждому мертвому спавним черепок и запрещаем интеракции
                this.spawnSkull(creature);
            }
        })

        if (this.isAllCreaturesDead(BattleStore.getEnemyAliveCreatures())) {
            // Если нет живых, то пользователь победил
            // Запрещаем любые интеракции
            this.disableAllInteractions();
        }

        this.battleService.showBattleResultPanel(this, this.canvas, (hasWon: boolean) => this.exitScene(BattleType.MOB, hasWon),
            this.mainThemeSound, this.isSpecialMusic ? this.levelWonSound : this.victorySound, this.defeatSound);
    };

    private getEnemies(bs: BattleStatus): Creature[] {
        let enemies: Creature[] = [];
        if (bs.battleType === BattleType.MULTI && PlayerStore.getPlayer().level < 3) {
            enemies = BattleStore.getEnemyCreatures().filter(c => BattleUtils.getTeam(c) === TeamTag.TM1);
        } else {
            enemies = BattleStore.getEnemyCreatures();
        }
        return enemies;
    }

    private allowPlayerCreatureActions(playerCreature: Creature) {

        let self = this;

        let playerImageContainer: CreaturePanel = this.creaturePanelById.get(playerCreature.id)!;

        playerImageContainer!.setInteractive({ draggable: true, dropZone: true})
        .on('dragstart', function(pointer: any) {

            self.cardArrow
                .setPosition(playerImageContainer.x, playerImageContainer.y)
                .setVisible(true);

            self.cardArrow.calculateRotationAngle(self.cardArrow.x, self.cardArrow.y, pointer.worldX, pointer.worldY);

            self.cardArrow.setDisplayHeight(playerImageContainer.displayHeight / 2);

            playerImageContainer.turnOnAttackerLook();

            self.chosenPlayerCreatureId = playerCreature.id;

            if (self.yourTurnMessagePanel) { self.yourTurnMessagePanel.setVisible(false); }

            self.lessonService.stopAndDeleteLesson();

        })
        .on('drag', function (pointer: any) {

            self.cardArrow.calculateRotationAngle(self.cardArrow.x, self.cardArrow.y, pointer.worldX, pointer.worldY);
            self.cardArrow.calculateDisplayHeight(playerImageContainer.x, playerImageContainer.y, pointer.worldX, pointer.worldY, 1.05);

        })
        .on('dragend',function(pointer: any) {

            if (!self.targetContainer) {
                self.cardArrow.setVisible(false);
                self.chosenPlayerCreatureId = null;
            }

            self.lessonService.showBattleLesson(self, self.canvas!.width / 2, self.canvas!.height / 2,
                self.myCreaturesGroup, self.enemyCreaturesGroup);

            playerImageContainer.turnOnDefaultLook();
        })
    }

    private allowEnemyCreatureActions(enemyCreature: Creature) {

        let self = this;

        let enemyImageContainer: CreaturePanel = this.creaturePanelById.get(enemyCreature.id)!;

        let over: boolean = false;
        let effectConts: (CreatureInteractionEffectTypePanel | Sprite)[] = [];
        enemyImageContainer!.setInteractive({dropZone: true})
        .on('pointerover',() => {
            this.targetContainer = enemyImageContainer;

            this.chosenEnemyCreatureId = enemyCreature.id;

            if (this.cardArrow.visible && !enemyImageContainer.isDefenderLook()) {
                enemyImageContainer.turnOnDefenderLook();

                over = true;
                BattleStore.precalculate(this.chosenPlayerCreatureId, this.chosenEnemyCreatureId).then((p: MovePrediction) => {
                    if (over) {
                        let attackerContainer = this.creaturePanelById.get(self.chosenPlayerCreatureId);
                        if (attackerContainer) {
                            for (let s of p.defenderSkillDeltas) {
                                let dead = s.skillType === SkillType.HEALTH && enemyImageContainer.getHealth() === s.skillDelta;
                                let cont = self.animateDamage(enemyImageContainer, s, true, dead);
                                if (cont) {
                                    effectConts.push(cont);
                                }
                            }
                            if (p.blockChancePercent > 0) {
                                effectConts.push(enemyImageContainer.showDefendPreview(BattleEffect.GOLEM_BLOCK));
                            }
                            if (p.attackerSkillDeltas) {
                                for (let s of p.attackerSkillDeltas) {
                                    let dead = s.skillType === SkillType.HEALTH && attackerContainer.getHealth() === s.skillDelta;
                                    let cont = self.animateDamage(attackerContainer, s, true, dead);
                                    if (cont) {
                                        effectConts.push(cont);
                                    }
                                }
                            }
                        }
                    }
                });
            }

        })
        .on('pointerout',() => {
            over = false;
            this.targetContainer = null;
            this.chosenEnemyCreatureId = null;
            enemyImageContainer.turnOnDefaultLook();
            for (let c of effectConts) {
                c.setVisible(false);
                c.destroy();
            }
            effectConts = [];
        })
        .on('pointerup',() => {
            for (let c of effectConts) {
                c.setVisible(false);
                c.destroy();
            }
            effectConts = [];

            enemyImageContainer.turnOnDefaultLook();

            if (this.cardArrow.visible) {

                let myAliveCreatures: Creature[] = BattleStore.getMyAliveCreatures();

                let playerAttackerId = this.chosenPlayerCreatureId;
                let botDefenderId = this.chosenEnemyCreatureId;

                if (myAliveCreatures.length > 0 && CreatureUtils.getUnitHealthCurrentValue(enemyCreature) > 0) {

                    let battleStatus: BattleStatus | undefined = undefined;

                    let attackerContainer = this.creaturePanelById.get(playerAttackerId)!;
                    let defenderContainer = this.creaturePanelById.get(botDefenderId)!;

                    let turnHappened = false;
                    let contactHappened = false;
                    let completeHappened = false;

                    let turnActiveAnimated = false;
                    let contactAnimated = false;
                    let completeAnimated = false;

                    let turnActiveDo = () => {
                        if (battleStatus && turnHappened && !turnActiveAnimated) {
                            turnActiveAnimated = true;
                            let bm = battleStatus.moves[this.moveNumber];

                            for (let ef of bm.effects) {
                                switch (ef) {
                                    case Effect.BLOCK_BLUE:
                                        defenderContainer.showDefendEffect(bm.battleEffect);
                                        break;
                                    case Effect.BLOCK_YELLOW:
                                        defenderContainer.showTruncateEffect();
                                        break;
                                    case Effect.BLOCK_RED:
                                        defenderContainer.showRedBlock();
                                        break;
                                    case Effect.BLOCK_CLAN:
                                        SoundUtils.addAndPlay(self, Keys.SOUND_USE_SCROLL, {volume: Settings.MUSIC_VOLUME});
                                        let clanName = BattleScene.getClanName(defenderContainer);
                                        if(clanName){
                                            new ClanShield(self, clanName, 100, defenderContainer.y - 150).show();
                                        }
                                        defenderContainer.showDefendEffect(bm.battleEffect);
                                        break;
                                }
                            }
                        }
                    };

                    let contactDo = () => {
                        if (battleStatus && contactHappened && !contactAnimated) {
                            contactAnimated = true;
                            let bm = battleStatus.moves[this.moveNumber];
                            // Анимируем повреждения противника в момент столкновения карт:
                            if (bm) {
                                bm.defenderSkillDeltas.forEach(skillDelta => {
                                    self.animateDamage(defenderContainer, skillDelta);
                                });
                                defenderContainer.setSkills(bm.defenderChangedSkills);

                                if (bm.lostDefenderForces) {
                                    defenderContainer.looseForces(bm.lostDefenderForces);
                                }
                            }
                        }
                    };

                    let completeDo = () => {
                        if (battleStatus && completeHappened && !completeAnimated) {
                            completeAnimated = true;
                            let bm = battleStatus.moves[this.moveNumber];

                            if (bm && bm.attackerSkillDeltas) {
                                bm.attackerSkillDeltas.forEach(skillDelta => {
                                    self.animateDamage(attackerContainer, skillDelta);
                                });
                            }
                            if (bm && bm.attackerChangedSkills) {
                                attackerContainer.setSkills(bm.attackerChangedSkills);
                            }

                            ++this.moveNumber;
                            self.processMoves(battleStatus);
                        }
                    };

                    let attackerCreature = BattleStore.getCreatureById(playerAttackerId)!;
                    self.animateFirstMove(attackerCreature, attackerContainer, defenderContainer, self.playerPunchSounds,
                        () => {
                            turnHappened = true;
                            turnActiveDo();
                        },
                        () => {
                            contactHappened = true;
                            contactDo();
                        },
                        () => {
                            completeHappened = true;
                            completeDo();
                        });

                    BattleStore.hit(playerAttackerId, botDefenderId).then((bs: BattleStatus | undefined) => {
                        if (bs) {
                            battleStatus = bs;

                            let attackerEffect = bs.moves[self.moveNumber].attackerEffect;
                            attackerEffect == AttackerEffect.LUCK && self.performLuckAnim(attackerContainer.x, attackerContainer.y);

                            self.lessonService!.setMarker();
                            turnActiveDo();
                            contactDo();
                            completeDo();
                        } else {
                            self.sceneService.startScene(this, SceneName.BATTLE_SCENE, this.canvas!.width, this.canvas!.height);
                        }
                    }).catch(() => {
                        ListenerManager.callConnectionLostCallback();
                    });
                }

                // Если отпустили ЛКМ - обнуляемся (с) ВВП
                this.chosenEnemyCreatureId = null;
                this.chosenPlayerCreatureId = null;
                this.cardArrow.visible = false;
                this.targetContainer = null;
            }
        })
    }

    private processMoves(bs: BattleStatus) {
        let self = this;
        let playerCreatures: Creature[] = BattleStore.getMyCreatures();
        let enemyCreatures: Creature[] = BattleStore.getEnemyCreatures();
        if (this.moveNumber < bs.moves.length) {
            let battleMove = bs.moves[this.moveNumber];

            let animate = () => {
                if (battleMove.attackerId) {
                    if (BattleScene.isPlayerMove(battleMove)) {
                        this.animatePlayerMove(battleMove, () => self.processMoves(bs));
                    } else {
                        this.animateEnemyMove(battleMove, () => self.processMoves(bs));
                    }
                } else {
                    this.animateIdleMove(battleMove, () =>  self.processMoves(bs));
                }
            }

            if (battleMove.phrases && battleMove.phrases.length > 0) {
                for (let p of battleMove.phrases) {
                    let cp = this.creaturePanelById.get(p.creatureId)!;
                    if (cp) {
                        self.time.delayedCall(p.delayMs, () => {
                            new PhrasePanel(self, cp.x, cp.y - 110).show(p.text);
                        });
                    }
                }
            }

            if (battleMove.cmnt && battleMove.cmnt.length > 0) {
                self.yourTurnMessagePanel.show(battleMove.cmnt.toUpperCase());
            }

            if (battleMove.delay && battleMove.delay > 0) {
                self.time.delayedCall(battleMove.delay, () => {
                    animate();
                });
            } else {
                animate();
            }

        } else {
            self.battleService.showBattleResultPanel(self, self.canvas, (hasWon: boolean) => self.exitScene(bs.battleType, hasWon),
                self.mainThemeSound, self.isSpecialMusic ? self.levelWonSound : self.victorySound, self.defeatSound);

            // Продолжаем игру, если у всех еще есть юниты
            playerCreatures.filter(currentCreature => CreatureUtils.getUnitHealthCurrentValue(currentCreature) > 0).forEach(aliveCreature => {
                self.allowPlayerCreatureActions(aliveCreature);
            })
            self.getEnemies(bs).filter(currentCreature => CreatureUtils.getUnitHealthCurrentValue(currentCreature) > 0).forEach(aliveCreature => {
                self.allowEnemyCreatureActions(aliveCreature);
            })

            self.showYourTurnMessage();

            // Разрешаем интеракции свитками и эликами
            self.battleService!.enableConsumablePanelInteractions();
            self.skipButtonEnabled = true;
        }
    }

    private static isPlayerMove(bm: BattleMove): boolean {
        return CreatureUtils.exists(bm.attackerId);
    }

    private animateIdleMove(bm: BattleMove, completeCallback: () => unknown) {
        let self = this;
        ++this.moveNumber;
        self.disableAllInteractions();
        let defenderContainer = this.creaturePanelById.get(bm.defenderId)!;
        if (bm.battleEffect === BattleEffect.RESURRECTION) {

            let blockImage = SpriteUtils.addSprite(this, defenderContainer.x / CU.S, defenderContainer.y / CU.S, Keys.ATLAS_UI, Keys.IMG_RESURRECT_EFFECT, 1).setDepth(defenderContainer.depth + 1);
            SoundUtils.addAndPlay(this, Keys.SOUND_RESURRECT, {volume: 0.6});
            if (bm.nextMove === NextMove.FAST || bm.nextMove === NextMove.HALF_FAST) {
                completeCallback();
            }
            let duration = PlayerStore.getPlayer().stair <= 500 ? 1000 : (PlayerStore.getPlayer().stair <= 750 ? 800 : 500);
            this.tweens.add({
                targets: blockImage,
                duration: duration,
                delay: 0,
                angle: 180,
                scale: 1.5,
                alpha: 0,
                onComplete: function () {
                    defenderContainer.ressurect();
                    if (bm.restoredDefenderForces) {
                        defenderContainer.restoreAllForces(bm.restoredDefenderForces);
                    }
                    self.doAnimateIdleMove(bm, defenderContainer, bm.nextMove === NextMove.DEFAULT ? completeCallback : undefined);
                }
            });
        } else {
            this.doAnimateIdleMove(bm, defenderContainer, completeCallback);
        }
    }

    private doAnimateIdleMove(bm: BattleMove, defenderContainer: CreaturePanel,
                              completeCallback: (() => unknown) | undefined) {
        let self = this;
        bm.defenderSkillDeltas.forEach(skillDelta => {
            self.animateDamage(defenderContainer, skillDelta);
        });
        defenderContainer.setSkills(bm.defenderChangedSkills);
        if (bm.lostDefenderForces) {
            defenderContainer.looseForces(bm.lostDefenderForces);
        }

        if (completeCallback) {
            completeCallback();
        }
    }

    private animatePlayerMove(bm: BattleMove, completeCallback: () => unknown) {
        let attackerContainer = this.creaturePanelById.get(bm.attackerId)!;
        let defenderContainer = this.creaturePanelById.get(bm.defenderId)!;

        this.animateMove(bm, attackerContainer, defenderContainer, 0, this.playerPunchSounds,
            completeCallback, () => {}, () => {});
    }

    private animateEnemyMove(bm: BattleMove, completeCallback: () => unknown) {
        let self = this;

        let attackerContainer = this.creaturePanelById.get(bm.attackerId)!;
        let defenderContainer = this.creaturePanelById.get(bm.defenderId)!;

        let cardTurnDuration = self.CARD_TURN_DURATION_MS * self.getCardTurnDurationMultiplier(attackerContainer, defenderContainer);

        this.animateMove(bm, attackerContainer, defenderContainer, self.CARD_FLY_UP_DURATION_MS, self.enemyPunchSounds,
            completeCallback,
            () => {
                // На старте увеличиваем глубину на 10, чтобы карта всегда была сверху, с середины пути
                // (delay + duration) / 2
                self.time.delayedCall((cardTurnDuration + self.CARD_FLY_UP_DURATION_MS) / 2, () => {
                    attackerContainer.depth += defenderContainer.depth + 1;
                });
            }, () => {
                self.time.delayedCall(self.CARD_RETURN_DURATION_MS - 20, () => {
                    attackerContainer.depth -= defenderContainer.depth + 1;
                });
            });
    }

    private animateMove(bm: BattleMove,
                        attackerContainer: CreaturePanel,
                        defenderContainer: CreaturePanel,
                        turnDelayMs: number,
                        punchSounds: (Phaser.Sound.BaseSound | undefined)[],
                        completeCallback: () => unknown,
                        onTurnStart: () => unknown,
                        onReturnStart: () => unknown) {
        ++this.moveNumber;
        let self = this;
        let flySpeedRatio: number = (bm.effects && bm.effects.includes(Effect.SPEED_X2)) ? 0.8 : 1.0;

        let ac: CreaturePanel | Container = attackerContainer;

        let shadow = false;
        let noContact = false;
        let easeMode = 'Quint.easeIn';

        if (bm.effects) {
            for (let e of bm.effects) {
                if (e === Effect.SHADOW) {
                    shadow = true;
                } else if (e === Effect.NO_CONTACT) {
                    noContact = true;
                } else if (e === Effect.QUADRATIC) {
                    easeMode = 'Easing.Quadratic.In';
                }
            }
        }

        if (shadow) {
            let shapeshift = bm.effects && bm.effects.includes(Effect.SHAPESHIFT);
            let cr: Creature = shapeshift ? defenderContainer.getCreature() : attackerContainer.getCreature();
            ac = new SimplifiedCreaturePanel(this, ac.x / CU.S, ac.y / CU.S, cr);
            SpriteUtils.scale(ac, 1 / CU.S);
            ac.setDepth(attackerContainer.depth + 1);
            ac.setAlpha(0.4)
            self.add.existing(ac);
        } else {
            ac = attackerContainer;
        }

        let cardShade = attackerContainer.getCardShade();

        let attackerCreature = BattleStore.getCreatureById(bm.attackerId);
        if (!attackerCreature) {
            return;
        }

        if (bm.nextMove === NextMove.ULTRA_FAST) {
            self.time.delayedCall(100, () => {
                completeCallback();
            });
        }
        // Анимация взлета карты:
        this.animateTakeOff(ac, cardShade, () => {
            if (noContact) {
                self.time.delayedCall(1400, () => {
                    animateDamage();
                    self.tweens.add({
                        targets: ac,
                        duration: self.CARD_RETURN_DURATION_MS * flySpeedRatio,
                        paused: true,
                        props: {
                            x: { value: ac.x - 19, ease: 'Linear' },
                            y: { value: ac.y + 14, ease: 'Linear' }
                        },

                        onStart: function () {
                            self.shadeAnimation(cardShade, -38, 28, self.CARD_RETURN_DURATION_MS * flySpeedRatio,'Linear');
                        },

                        onComplete: function () {
                            self.time.delayedCall(800, () => {
                                completeCallback();
                            });
                        }
                    }).play();
                });
            } else {
                cardTurn.play();
            }
        }, flySpeedRatio);

        let animateDamage = () => {
            bm.defenderSkillDeltas.forEach(skillDelta => {
                self.animateDamage(defenderContainer, skillDelta);
            });
            defenderContainer.setSkills(bm.defenderChangedSkills);
            if (bm.lostDefenderForces) {
                defenderContainer.looseForces(bm.lostDefenderForces);
            }
        };


        let cardTurnDuration = self.CARD_TURN_DURATION_MS * self.getCardTurnDurationMultiplier(ac, defenderContainer) * flySpeedRatio;

        // Анимация полета к карте противника:
        let cardTurn = this.tweens.add({
            targets: ac,
            duration: cardTurnDuration,
            delay: turnDelayMs,
            paused: true,
            props: {
                x: { value: defenderContainer.x, ease: easeMode },
                y: { value: defenderContainer.y, ease: 'Quint.easeIn' }
            },

            onStart: function() {
                onTurnStart();

                SoundUtils.play(self.windDownSound);
                self.time.delayedCall(cardTurnDuration - 70, () =>{
                    SoundUtils.stop(self.windDownSound);
                    let sound = SoundUtils.getAttackSoundByPower(punchSounds, CreatureUtils.getUnitAttackValue(attackerCreature!));
                    SoundUtils.play(sound);
                });

                // Имитация возврата тени к дефолтному положению
                self.shadeAnimation(cardShade, -38, 28, cardTurnDuration,'Quint.easeIn');

                if (bm.nextMove === NextMove.FAST) {
                    completeCallback();
                }
            },

            onActive: function () {
                for (let ef of bm.effects) {
                    switch (ef) {
                        case Effect.BLOCK_BLUE:
                            defenderContainer.showDefendEffect(bm.battleEffect);
                            break;
                        case Effect.BLOCK_YELLOW:
                            defenderContainer.showTruncateEffect();
                            break;
                        case Effect.BLOCK_RED:
                            defenderContainer.showRedBlock();
                            break;
                        case Effect.BLOCK_CLAN:
                            SoundUtils.addAndPlay(self, Keys.SOUND_USE_SCROLL, {volume: Settings.MUSIC_VOLUME});
                            let clanName = BattleScene.getClanName(defenderContainer);
                            if(clanName){
                                new ClanShield(self, clanName, 100, defenderContainer.y - 150).show();
                            }
                            defenderContainer.showDefendEffect(bm.battleEffect);
                            break;
                    }
                }
            },

            onComplete: function() {
                // Анимируем повреждения противника в момент столкновения карт:
                animateDamage();

                cardReturn.play();
            },

        });

        let cardReturn = this.tweens.add({
            targets: ac,
            duration: self.CARD_RETURN_DURATION_MS * flySpeedRatio,
            paused: true,
            props: {
                x: { value: ac.x, ease: 'Linear' },
                y: { value: ac.y, ease: 'Linear' }
            },

            onStart: function () {
                onReturnStart();

                self.shadeAnimation(cardShade, -38, 28, self.CARD_RETURN_DURATION_MS * flySpeedRatio,'Linear');

                if (bm.nextMove === NextMove.HALF_FAST) {
                    completeCallback();
                }
            },

            onComplete: function () {

                if (shadow) {
                    ac.setVisible(false);
                    ac.destroy();
                    ac = attackerContainer;
                }

                SoundUtils.stop(self.windDownSound);

                // Анимируем свои повреждения (если такие имеются) после возврата карты на место:
                if (bm && bm.attackerSkillDeltas) {
                    bm.attackerSkillDeltas.forEach(skillDelta => {
                        self.animateDamage(attackerContainer, skillDelta);
                    });
                }
                if (bm && bm.attackerChangedSkills) {
                    attackerContainer.setSkills(bm.attackerChangedSkills);
                }

                if (bm.nextMove === NextMove.DEFAULT) {
                    completeCallback();
                }
            }
        });
    }
    private getCardTurnDurationMultiplier(attackerContainer:Phaser.GameObjects.Container, defenderContainer:Phaser.GameObjects.Container){
        let sidesMinDistance = 270;
        let cardsDistance = attackerContainer.y == defenderContainer.y ? Math.abs(attackerContainer.x - defenderContainer.x) : sidesMinDistance;
        return cardsDistance < sidesMinDistance ? Math.max(0.5, cardsDistance/sidesMinDistance) : 1;
    }

    private animateFirstMove(attackerCreature: Creature,
                               attackerContainer: CreaturePanel,
                               defenderContainer: CreaturePanel,
                               punchSounds: (Phaser.Sound.BaseSound | undefined)[],
                               onTurnActive: () => unknown,
                               onReturnStart: () => unknown,
                               completeCallback: () => unknown) {
        let self = this;

        let ac: CreaturePanel | Container = attackerContainer;
        let shadow = attackerCreature.level > 5 && CreatureUtils.hasForce(attackerCreature, WarriorClass.SHADOW);

        if (shadow) {
            let cr: Creature = attackerContainer.getCreature();
            ac = new SimplifiedCreaturePanel(this, ac.x / CU.S, ac.y / CU.S, cr);
            SpriteUtils.scale(ac,  1 / CU.S);
            ac.setDepth(attackerContainer.depth + 1);
            ac.setAlpha(0.4)
            self.add.existing(ac);
        } else {
            ac = attackerContainer;
        }

        let cardShade = attackerContainer.getCardShade();

        this.tweens.add({
            targets: ac,
            duration: self.CARD_FLY_UP_DURATION_MS,
            ease: 'Linear',
            props: {
                x: { value: ac.x + 10 },
                y: { value: ac.y - 15 },
            },
            onStart: function () {
                self.disableAllInteractions();
                // Имитация сдвига тени влево вниз
                self.shadeAnimation(cardShade, -75, 54, self.CARD_FLY_UP_DURATION_MS, 'Linear');
            },
            onComplete: function () {
                cardTurn.play();
            }
        });

        let cardTurnDuration = self.CARD_TURN_DURATION_MS * self.getCardTurnDurationMultiplier(ac, defenderContainer);

        // Анимация полета к карте противника:
        let cardTurn = this.tweens.add({
            targets: ac,
            duration: cardTurnDuration,
            delay: 0,
            paused: true,
            props: {
                x: { value: defenderContainer.x, ease: 'Quint.easeIn' },
                y: { value: defenderContainer.y, ease: 'Quint.easeIn' }
            },

            onStart: function() {
                SoundUtils.play(self.windDownSound);
                self.time.delayedCall(cardTurnDuration - 70, () =>{
                    SoundUtils.stop(self.windDownSound);
                    let sound = SoundUtils.getAttackSoundByPower(punchSounds, CreatureUtils.getUnitAttackValue(attackerCreature));
                    SoundUtils.play(sound);
                });

                // Имитация возврата тени к дефолтному положению
                self.shadeAnimation(cardShade, -38, 28, cardTurnDuration,'Quint.easeIn');
            },

            onActive: function () {
                onTurnActive();
            },

            onComplete: function() {
                cardReturn.play();
            },

        });

        let cardReturn = this.tweens.add({
            targets: ac,
            duration: self.CARD_RETURN_DURATION_MS,
            paused: true,
            props: {
                x: { value: ac.x, ease: 'Linear' },
                y: { value: ac.y, ease: 'Linear' }
            },

            onStart: function () {
                onReturnStart();

                self.shadeAnimation(cardShade, -38, 28, cardTurnDuration,'Linear');
            },

            onComplete: function () {
                if (shadow) {
                    ac.setVisible(false);
                    ac.destroy();
                    ac = attackerContainer;
                }

                SoundUtils.stop(self.windDownSound);
                completeCallback();
            }
        });
    }

    private performLuckAnim(x: number, y: number, completeCallback?: () => unknown): void {
        const self = this;

        self.luckyClover.setX(x).setY(y - 30);

        if (!self.luckyCloverAnim) {
            self.luckyCloverAnim = this.tweens.add({
                targets: self.luckyClover,
                y: { start: self.luckyClover.y, to: self.luckyClover.y - 80, duration: 600 },
                scale: { start: 1, to: 1.5, duration: 450, delay: 0 },
                alpha: { start: 1, to: 0, duration: 200, delay: 500 },
                repeat: 0,
                persist: true,
                onComplete: () => { completeCallback && completeCallback(); }
            });
        } else self.luckyCloverAnim.play();
    }

    private animateTakeOff(attackerContainer: Container, cardShade: Image, completeCallback: () => unknown, flySpeedRatio: number) {
        let self = this;
        this.tweens.add({
            targets: attackerContainer,
            duration: self.CARD_FLY_UP_DURATION_MS * flySpeedRatio,
            ease: 'Linear',
            props: {
                x: { value: attackerContainer.x + 10 },
                y: { value: attackerContainer.y - 15 },
            },
            onStart: function () {
                self.disableAllInteractions();
                // Имитация сдвига тени влево вниз
                self.shadeAnimation(cardShade, -75, 54, self.CARD_FLY_UP_DURATION_MS, 'Linear');
            },
            onComplete: function () {
                completeCallback();
            }
        });
    }

    private shadeAnimation(shade: Image, x: number, y: number, duration: number, ease: string, delay?: number) {
        return this.tweens.add({
            targets: shade,
            duration: duration,
            ease: ease,
            delay: delay != null ? delay : 0,
            props: {
                x: { value: x },
                y: { value: y }
            },
        });
    }

    private disableAllInteractions() {
        BattleStore.getMyCreatures().forEach((c: Creature) => { this.creaturePanelById.get(c.id)!.disableInteractive(); })
        BattleStore.getEnemyCreatures().forEach((c: Creature) => { this.creaturePanelById.get(c.id)!.disableInteractive();})

        this.battleService.disableConsumablePanelInteractions();
        this.skipButtonEnabled = false;
    }

    private refreshCreaturesHealth(creatures: any) {
        creatures.forEach((c: Creature) => {
            let creaturePanel = this.creaturePanelById.get(c.id);
            creaturePanel!.setHealth(CreatureUtils.getUnitHealth(c));
        })
    }

    private spawnSkull(creature: Creature) {

        let container = this.creaturePanelById.get(creature.id)!;
        container.die();
    }

    private isAllCreaturesDead (creatures: Creature[]) {
        return creatures.length == 0;
    }

    private animateDamage(creaturePanel: CreaturePanel, skillDelta: SkillDelta,
                          preview: boolean = false, dead: boolean = false): CreatureInteractionEffectTypePanel | undefined {
        if (skillDelta.skillDelta === 0) {
            return undefined;
        }

        let x = creaturePanel.x / CU.S;
        let y = creaturePanel.y / CU.S;
        let depth = creaturePanel.depth + 1;
        let skillDeltaText = (skillDelta.skillDelta > 0 ? "-" : '+') + TextUtils.formatMoney(Math.abs(Math.ceil(skillDelta.skillDelta)));
        let toAlpha = 0;

        let damageEffect: CreatureInteractionEffectTypePanel = new CreatureInteractionEffectTypePanel(this, x, y, skillDeltaText, skillDelta.skillType,
            depth, toAlpha, 80, 300, 50, () => {
                damageEffect.setVisible(false);
                damageEffect.destroy();
            }, preview, dead);
        this.add.existing(damageEffect);
        return damageEffect;

    }

    private setGroupPanelsDepth(group: Group, initDepth: number) {
        let cp: CreaturePanel[] = group.getChildren() as CreaturePanel[];
        for (let panel of cp) {
            panel.setDepth(initDepth -= 1);
        }
    }

    private makeArrow() {
        let arr = new Arrow(this)
            .setOrigin(0.5, 1)
            .setDepth(this.ARROW_DEPTH)
            .setVisible(false);
        SpriteUtils.setDispSize(arr, 128 * CU.S, 0);
        return arr;
    }

    private exitScene(battleType: BattleType | undefined, victory: boolean) {
        SoundUtils.stop(this.mainThemeSound);
        SoundUtils.play(this.clickSound);
        if (PlayerStore.getPlayer().level < 2) {
            PlaceService.reset();
        }
        let sceneName = battleType === BattleType.ARENA ? SceneName.ARENA_SCENE : SceneName.ROAD_SCENE;
        BattleStore.complete(victory).then( () => {
            if (this.scene.isActive()) {
                this.sceneService.startScene(this, sceneName, this.canvas!.width, this.canvas!.height);
            }
        });
    }

    private showYourTurnMessage() {
        let bs = BattleStore.getBattleStatus();
        if (!this.battleResultWindowContainer && bs && bs.battleState != "COMPLETED") {
            this.yourTurnMessagePanel.show();
        }
    }

    private static getClanName(defenderContainer:CreaturePanel){
        let player = PlayerStore.getPlayer();
        if (defenderContainer.getCreature().playerId !== player.id){
            return BattleStore.getBattleStatus()?.enemyClanName;
        }
        return player.clanName;
    }

    public reload(): void {
        this.sceneService.startSceneInstant(this, SceneName.BATTLE_SCENE);
    }
}
